/**
 * Paper copies of a voucher or an invoice: a plain page opened in a new
 * window and handed to the browser's print dialog. Arabic and English side
 * by side, since the paper goes to people who read either.
 */

import { docNo, money } from "./api";

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const TITLES = {
  receipt: ["سند قبض", "Receipt Voucher"],
  payment: ["سند صرف", "Payment Voucher"],
  sales: ["فاتورة مبيعات", "Sales Invoice"],
  purchase: ["فاتورة مشتريات", "Purchase Invoice"],
};

const STYLE = `
  * { box-sizing: border-box; }
  body { font-family: "Segoe UI", Tahoma, Arial, sans-serif; color: #0b1b3a; margin: 32px; }
  .head { display: flex; justify-content: space-between; align-items: center;
          border-bottom: 3px solid #c9a227; padding-bottom: 12px; margin-bottom: 20px; }
  .brand { font-size: 22px; font-weight: 800; letter-spacing: 2px; }
  .title { text-align: center; }
  .title b { display: block; font-size: 22px; }
  .title span { font-size: 14px; color: #555; }
  .no { text-align: right; font-size: 14px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { border: 1px solid #d9d9d9; padding: 8px 10px; font-size: 14px; }
  th { background: #f6f3ea; text-align: start; width: 30%; }
  .items th { width: auto; text-align: center; }
  .items td.n { text-align: right; white-space: nowrap; }
  .amount { font-size: 20px; font-weight: 800; }
  .sign { display: flex; justify-content: space-between; margin-top: 64px; font-size: 13px; }
  .sign div { border-top: 1px solid #999; padding-top: 6px; width: 30%; text-align: center; }
  @media print { body { margin: 12mm; } }
`;

function open(title, body) {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    // eslint-disable-next-line no-alert
    window.alert("Allow pop-ups for this site to print");
    return;
  }
  w.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
      `<style>${STYLE}</style></head><body>${body}` +
      `<script>window.onload = function () { window.print(); };</script></body></html>`
  );
  w.document.close();
}

function header(type, number, date) {
  const [ar, en] = TITLES[type];
  return `<div class="head">
      <div class="brand">TRUST CARGO</div>
      <div class="title"><b>${ar}</b><span>${en}</span></div>
      <div class="no">No. <b>${esc(docNo(type, number))}</b><br/>Date: <b>${esc(date)}</b></div>
    </div>`;
}

const row = (ar, en, value) => `<tr><th>${ar} / ${en}</th><td>${value}</td></tr>`;

export function printVoucher(v) {
  const receipt = v.type === "receipt";
  const body = `${header(v.type, v.number, v.date)}
    <table>
      ${row(
        receipt ? "استلمنا من" : "صرفنا إلى",
        receipt ? "Received from" : "Paid to",
        esc(v.party_name || "-")
      )}
      ${row(
        "المبلغ",
        "Amount",
        `<span class="amount">${money(v.amount)} ${esc(v.currency)}</span>`
      )}
      ${row(receipt ? "إلى" : "من", receipt ? "Into" : "From", esc(v.box || "-"))}
      ${row("الفئة / الحساب", "For", esc(v.category || "-"))}
      ${
        v.invoice_number
          ? row("عن فاتورة", "For invoice", esc(docNo(v.invoice_type, v.invoice_number)))
          : ""
      }
      ${v.method ? row("طريقة الدفع", "Method", esc(v.method)) : ""}
      ${row("المرجع", "Reference", esc(v.reference || "-"))}
      ${row("البيان", "Description", esc(v.description || "-"))}
    </table>
    <div class="sign"><div>المحاسب / Accountant</div><div>المستلم / Receiver</div><div>المدير / Manager</div></div>`;

  open(docNo(v.type, v.number), body);
}

export function printInvoice(inv) {
  const lines = (inv.items || [])
    .map(
      (it, i) => `<tr><td style="text-align:center">${i + 1}</td><td>${esc(it.description)}</td>
        <td class="n">${Number(it.qty)}</td><td class="n">${money(
        it.price
      )}</td><td class="n">${money(it.total)}</td></tr>`
    )
    .join("");

  const sum = (ar, en, v, strong) =>
    `<tr><td colspan="4" style="text-align:end">${ar} / ${en}</td>
      <td class="n" ${strong ? 'style="font-weight:800;font-size:16px"' : ""}>${money(v)} ${esc(
      inv.currency
    )}</td></tr>`;

  const body = `${header(inv.type, inv.number, inv.date)}
    <table>
      ${row(
        inv.type === "sales" ? "العميل" : "المورد",
        inv.type === "sales" ? "Customer" : "Supplier",
        esc(inv.party_name || "-")
      )}
      ${inv.due_date ? row("تاريخ الاستحقاق", "Due date", esc(inv.due_date)) : ""}
      ${inv.reference ? row("المرجع", "Reference", esc(inv.reference)) : ""}
    </table>
    <table class="items">
      <tr><th>#</th><th>البيان / Description</th><th>الكمية / Qty</th><th>السعر / Price</th><th>المجموع / Total</th></tr>
      ${lines}
      ${sum("المجموع الفرعي", "Subtotal", inv.subtotal)}
      ${Number(inv.discount) ? sum("الخصم", "Discount", -inv.discount) : ""}
      ${Number(inv.tax) ? sum(`الضريبة ${Number(inv.tax_rate)}%`, "Tax", inv.tax) : ""}
      ${sum("الإجمالي", "Total", inv.total, true)}
      ${sum("المدفوع", "Paid", inv.paid)}
      ${sum("المتبقي", "Balance due", inv.remaining, true)}
    </table>
    ${inv.notes ? `<p style="margin-top:16px"><b>ملاحظات / Notes:</b> ${esc(inv.notes)}</p>` : ""}
    <div class="sign"><div>المحاسب / Accountant</div><div>${
      inv.type === "sales" ? "العميل / Customer" : "المورد / Supplier"
    }</div><div>المدير / Manager</div></div>`;

  open(docNo(inv.type, inv.number), body);
}
